import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getErrorCode } from './errorCodes';
import ErrorIcon from './ErrorIcon';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import { saveLanguageToSession } from '../../utils/resolveInitialLanguage';
import styles from './styles.module.scss';
import layoutStyles from '../Layout/styles.module.scss';

const ErrorScreen = ({ errorType, tenant = 'default' }) => {
  const { t, i18n } = useTranslation();
  const [ready, setReady] = useState(false);
  const [language, setLanguage] = useState(i18n.language || 'en');
  const isMobile = window.innerWidth <= 800;
  const code = getErrorCode(errorType, tenant);

  const loadTranslationsForLang = useCallback(async (lang) => {
    const bundle = i18n.getResourceBundle(lang, 'translation');
    const hasRealBundle = bundle && Object.keys(bundle).length > 0;

    if (!hasRealBundle) {
      try {
        const version = process.env.REACT_APP_BUILD_TIME || Date.now();
        const response = await fetch(
          `./assets/translations/${lang}.json?v=${version}`
        );
        if (response.ok) {
          const translations = await response.json();
          i18n.addResourceBundle(lang, 'translation', translations, true, true);
        }
      } catch (err) {
        console.warn('Could not load translations for ErrorScreen');
      }
    }
  }, [i18n]);

  const changeLanguage = useCallback(async (lang) => {
    await loadTranslationsForLang(lang);
    await i18n.changeLanguage(lang);
    setLanguage(lang);
    saveLanguageToSession(lang);
  }, [i18n, loadTranslationsForLang]);

  useEffect(() => {
    const init = async () => {
      await loadTranslationsForLang(language);
      setReady(true);
    };
    init();
  }, [language, loadTranslationsForLang]);

  if (!ready) return null;

  return (
    <div className={styles.errorWrapper}>
      <div className={layoutStyles.header}>
        <img
          alt="logo"
          className={layoutStyles.logo}
          src={`/assets/${tenant}/logo.svg`}
        />
        <LanguageSelector
          currentLanguage={language}
          onChangeLanguage={changeLanguage}
        />
      </div>
      <div className={styles.main}>
        <ErrorIcon className={isMobile ? styles.mobileImage : styles.image} />
        <p className={styles.title}>{t(`codeErrors.${code}.title`)}</p>
        <p className={styles.subtitle}>{t(`codeErrors.${code}.message`)}</p>
        <p className={styles.code}>{t('general.reference', { code })}</p>
      </div>
    </div>
  );
};

export default ErrorScreen;
